/* eslint-disable react-hooks/exhaustive-deps */
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { createContext, ReactNode, useEffect, useState } from "react";
import * as Yup from "yup";
import useSnackbar from "@/hooks/useSnackbar";
import { Circle, CircleCheck } from "lucide-react";
import { useLocation } from "react-router-dom";
import useRoutes from "@/hooks/useRoutes";
import { useCreateProfileMutation, useLazyFetchProfileQuery } from "@/api/usersApi";
import { useCreateAccessMutation, useLazyFetchAccessQuery } from "@/api/accessApi";
import { useCreateOrganizationMutation, useDeleteOrganizationMutation, useUpdateOrganizationMutation } from "@/features/organizations/api/organizationsApi";
import { useAddMenuMutation, useLazyFetchMenusQuery } from "@/features/organizations/api/menusApi";
import { DEFAULT_MENU_TEMPLATE, DEFAULT_WEBSITE_TEMPLATE } from "@/constants/configs";
import { useAddContentMutation, useLazyFetchContentQuery } from "@/features/organizations/api/contentApi";
import dishes from "../exampleDishes.json";
import {
  useCreateWebsiteMutation,
  useCreateOrganizationAliasMutation,
  useLazyFetchOrganizationWebsitesQuery,
  useLazyFetchOrganizationAliasQuery,
} from "@/features/organizations/api/websiteApi";
import { Organization } from "@/types";
import { useAppDispatch } from "@/hooks";
import { uploadOrganizationImage } from "@/features/organizations/slice";
export interface OnboardingFormValues {
  industry: string;
  alias: string;
  title: string;
  primaryColor: string;
  secondaryColor: string;
  facebook?: string;
  instagram?: string;
  x?: string;
  tiktok?: string;
  whatsapp: string;
}
export interface Step {
  title?: string;
  label: string;
  component: ReactNode;
  hideContinueButton?: boolean;
  schema?: Yup.ObjectSchema<any>;
}

interface OnboardingContext {
  steps: Step[];
  validateStep: (stepId: string) => void;
}

export const OnboardingContext = createContext<OnboardingContext>({
  steps: [],
  validateStep: () => undefined,
});

interface Task {
  id: string;
  label: string;
  isCompleted: boolean;
}

const TASKS: Task[] = [
  {
    id: "user",
    label: "Creando usuario",
    isCompleted: false,
  },
  {
    id: "organization",
    label: "Construyendo tu restaurante",
    isCompleted: false,
  },
  {
    id: "menu",
    label: "Diseñando el menú",
    isCompleted: false,
  },
  {
    id: "dishes",
    label: "Preparando primeros platos",
    isCompleted: false,
  },
  {
    id: "website",
    label: "Activando tu sitio web",
    isCompleted: false,
  },
  {
    id: "profile",
    label: "Publicando el perfil",
    isCompleted: false,
  },
];

const SetupStep = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [openSnackbar] = useSnackbar();
  const [navigate] = useRoutes();
  const [activeTask, setActiveTask] = useState(0);
  const [tasks, setTasks] = useState<Task[]>(TASKS);
  const task = tasks[activeTask];

  // profiles
  const [fetchProfile, profile] = useLazyFetchProfileQuery();
  const [addProfile] = useCreateProfileMutation();
  // access
  const [fetchAccess] = useLazyFetchAccessQuery();
  const [addAccess] = useCreateAccessMutation();
  // organizations
  const [addOrganization, organization] = useCreateOrganizationMutation();
  const [deleteOrganization] = useDeleteOrganizationMutation();
  const [updateOrganization] = useUpdateOrganizationMutation();
  // menus
  const [fetchMenus] = useLazyFetchMenusQuery();
  const [addMenu] = useAddMenuMutation();
  // dishes
  const [fetchDishes] = useLazyFetchContentQuery();
  const [addDish] = useAddContentMutation();
  // websites
  const [fetchWebsites] = useLazyFetchOrganizationWebsitesQuery();
  const [addWebsite] = useCreateWebsiteMutation();
  // profile
  const [fetchAlias] = useLazyFetchOrganizationAliasQuery();
  const [addAlias, alias] = useCreateOrganizationAliasMutation();

  const completeTask = (taskIndex?: number) => {
    const draft = [...tasks];

    draft.splice(taskIndex ?? activeTask, 1, {
      ...task,
      isCompleted: true,
    });

    setTasks(draft);
    setActiveTask((prev) => prev + 1);
  };

  const createUserProfile = async () => {
    const userId = location.state?.user?.uid ?? null;

    if (!userId) return;

    const profile = await fetchProfile(userId);

    if (profile?.error?.status === 404) {
      const profile = await addProfile(location.state.user);
      if (!profile.error) completeTask();
    } else if (profile?.data) {
      completeTask();
    }
  };

  const createOrganization = async () => {
    const userId = location.state?.user?.uid ?? null;

    if (!userId) return;

    const isSuperAdmin = profile.data?.role === 'super-admin';

    const access = await fetchAccess(userId);

    if (access?.error?.status == 404 || isSuperAdmin) {
      const payload: Partial<Organization> = {
        title: location.state?.title,
        primaryColor: location.state?.primaryColor,
        secondaryColor: location.state?.secondaryColor,
        industry: "food-and-beverages",
      };

      if (location.state?.facebook) payload.facebook = location.state?.facebook;
      if (location.state?.instagram) payload.instagram = location.state?.instagram;
      if (location.state?.x) payload.x = location.state?.x;
      if (location.state?.tiktok) payload.tiktok = location.state?.tiktok;

      const organization = await addOrganization(payload);

      if (organization.error) {
        openSnackbar("Error creando comercio", "error");
      } else {
        const access = await addAccess({
          organizationId: organization.data?.id,
          organizationTitle: organization.data?.title,
        });

        if (access.error) {
          openSnackbar("Error creando comercio", "error");
          await deleteOrganization(organization.data?.id);
        } else if (organization?.data?.id) {
          // Upload logos
          const logosPayload = {
            logo: null,
            isotype: null,
          }

          if (location.state?.logo) {
            const logo = await dispatch(uploadOrganizationImage({
              organizationId: organization.data.id,
              file: location.state?.logo
            }));

            if (logo?.payload?.image) {
              logosPayload.logo = logo?.payload?.image;
            }
          }

          if (location.state?.isotype) {
            const isotype = await dispatch(uploadOrganizationImage({
              organizationId: organization.data.id,
              file: location.state?.isotype
            }));

            if (isotype?.payload?.image) {
              logosPayload.isotype = isotype?.payload?.image;
            }
          }

          await updateOrganization({
            organizationId: organization.data.id,
            payload: logosPayload,
          });
        }
      }
    }

    completeTask();
  };

  const createMenu = async () => {
    if (!organization?.data?.id) return;

    const results = await fetchMenus(organization?.data?.id);

    if (results?.error?.status === 404) {
      const menu = await addMenu({
        draft: {
          title: "Menú Principal",
          description: "Menú de ejemplo, editalo o elimínalo lo nates posible",
          isPublished: true,
          template: DEFAULT_MENU_TEMPLATE,
        },
        organizationId: organization?.data?.id,
      });

      if (menu.error) {
        openSnackbar("Error creando menú", "error");
      } else {
        completeTask();
      }
    } else {
      completeTask();
    }
  };

  const createDishes = async () => {
    if (!organization?.data?.id) return;

    // verify if org has content
    const results = await fetchDishes(organization?.data?.id);

    if (results?.error?.status === 404) {
      const sampleData = dishes[location.state?.industry] ?? [];

      try {
        await Promise.all(
          sampleData.map(async (dish) => {
            await addDish({ draft: dish, organizationId: organization?.data?.id });
          })
        );

        completeTask();
      } catch (error) {
        openSnackbar("Error creando contenido", "error");
      }
    } else if (results?.data) {
      completeTask();
    }
  };

  const createWebsite = async () => {
    if (!organization?.data?.id) return;

    const results = await fetchWebsites(organization.data.id);

    if (results?.error?.status === 404) {
      const website = await addWebsite({
        organizationId: organization.data.id,
        website: {
          title: "Sitio principal",
          template: DEFAULT_WEBSITE_TEMPLATE,
          isPublished: true,
        },
      });

      if (website.error) {
        openSnackbar("Error creando sitio web", "error");
      } else {
        await updateOrganization({
          organizationId: organization.data.id,
          payload: {
            website: website.data, // website id
          },
        });

        completeTask();
      }
    } else {
      completeTask();
    }
  };

  const createProfile = async () => {
    const alias = location.state?.alias ?? null;

    if (!organization?.data?.id || !alias) return;

    const results = await fetchAlias(alias);

    if (results?.error?.status === 404) {
      const result = await addAlias({
        organizationId: organization.data.id,
        alias,
      });

      if (result.error) {
        openSnackbar("Error creando el perfil", "error");
      } else {
        await updateOrganization({
          organizationId: organization.data.id,
          payload: {
            alias,
          },
        });

        completeTask();
      }
    } else {
      completeTask();
    }
  };

  useEffect(() => {
    switch (task?.id) {
      case "user":
        createUserProfile();
        break;
      case "organization":
        createOrganization();
        break;
      case "menu":
        createMenu();
        break;
      case "dishes":
        createDishes();
        break;
      case "website":
        createWebsite();
        break;
      case "profile":
        createProfile();
        break;
      default:
        return;
    }
  }, [task?.id]);

  useEffect(() => {
    if (!location.state) {
      navigate("onboarding.main");
    }
  }, [location.state]);

  useEffect(() => {
    if (tasks.every((task) => task.isCompleted)) {
      navigate("onboarding.finished", {}, {
        state: alias?.data
      });
    }
  }, [tasks, organization]);

  return (
    <Stack spacing={1}>
      {tasks.map((task, taskIndex) => {
        const isActivestep = activeTask === taskIndex;

        return (
          <Box key={task.id} sx={{ paddingX: 3, paddingY: 2.5, borderRadius: 3, backgroundColor: isActivestep ? "rgba(255,255,255,0.1)" : "transparent" }}>
            <Stack spacing={2} direction="row" justifyContent="space-between">
              <Typography variant="h4" color="white">
                {task.label}
              </Typography>
              {isActivestep ? (
                <CircularProgress color="secondary" size={24} />
              ) : task.isCompleted ? (
                <CircleCheck color="#D0F857" />
              ) : (
                <Circle color="rgba(255,255,255,0.25)" />
              )}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

export default SetupStep;
